import { useState, useEffect, useMemo, useRef } from "react";
import { social, getPlatformInfo, type SocialAccount } from "../lib/social";
import { navigate } from "../lib/router";
import { PlatformIcon } from "../components/PlatformIcon";
import { useAccounts } from "../lib/app-data";

function PlatformBadge({ platform }: { platform: string }) {
  const info = getPlatformInfo(platform);
  return (
    <span className={`w-6 h-6 ${info.color} text-white rounded flex items-center justify-center flex-shrink-0`}>
      <PlatformIcon platform={platform} className="w-3.5 h-3.5" />
    </span>
  );
}

interface MediaItem {
  url: string;
  storageId?: number;
  type: "image" | "video";
  name: string;
  uploading?: boolean;
  error?: string;
}

/** Upload through the Apteva Storage app and retain its stable file id. */
async function uploadFile(file: File): Promise<{ url: string; type: "image" | "video"; storageId: number }> {
  const base64 = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve((reader.result as string).split(",")[1]);
    reader.onerror = reject;
  });

  const res = await fetch("/api/storage/upload", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      file_name: file.name,
      file_data: base64,
      mime_type: file.type,
    }),
  });

  const data = await res.json();
  if (!data.success) throw new Error(data.error || "Upload failed");

  const fileData = data.data;
  const url = fileData.public_url || fileData.file_url || fileData.storage_url;
  const type = file.type.startsWith("video/") ? "video" as const : "image" as const;
  return { url, type, storageId: Number(fileData.id || fileData.file_id) };
}

const YOUTUBE_CATEGORIES = [
  { id: "22", name: "People & Blogs" },
  { id: "24", name: "Entertainment" },
  { id: "28", name: "Science & Technology" },
  { id: "26", name: "Howto & Style" },
  { id: "27", name: "Education" },
  { id: "10", name: "Music" },
  { id: "20", name: "Gaming" },
  { id: "17", name: "Sports" },
  { id: "19", name: "Travel & Events" },
  { id: "25", name: "News & Politics" },
  { id: "1", name: "Film & Animation" },
  { id: "23", name: "Comedy" },
  { id: "15", name: "Pets & Animals" },
  { id: "2", name: "Autos & Vehicles" },
];

export function Compose() {
  const { accounts: allAccounts, loading: accountsLoading } = useAccounts();
  const accounts = useMemo<SocialAccount[]>(
    () => allAccounts.filter((a) => a.status === "active"),
    [allAccounts],
  );
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [hasAutoSelected, setHasAutoSelected] = useState(false);
  const [text, setText] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [publishing, setPublishing] = useState(false);
  const [submitMode, setSubmitMode] = useState<"draft" | "publish">("publish");
  const [error, setError] = useState("");
  const loading = accountsLoading;
  const [dragging, setDragging] = useState(false);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const [platformOverrides, setPlatformOverrides] = useState<Record<string, any>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  function setPlatformField(platform: string, field: string, value: any) {
    setPlatformOverrides(prev => ({
      ...prev,
      [platform]: { ...prev[platform], [field]: value }
    }));
  }

  // Default to all active accounts selected once they're loaded — but only on
  // first arrival, so toggling a single account off doesn't snap them all back on.
  useEffect(() => {
    if (!hasAutoSelected && accounts.length > 0) {
      setSelectedIds(accounts.map((a) => a.id));
      setHasAutoSelected(true);
    }
  }, [accounts, hasAutoSelected]);

  function toggleAccount(id: number) {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  function selectAll() {
    setSelectedIds(accounts.map((a) => a.id));
  }

  function selectNone() {
    setSelectedIds([]);
  }

  async function handleFiles(files: FileList | File[]) {
    const fileArray = Array.from(files).filter(
      (f) => f.type.startsWith("image/") || f.type.startsWith("video/")
    );

    for (const file of fileArray) {
      const type = file.type.startsWith("video/") ? "video" as const : "image" as const;
      const placeholder: MediaItem = {
        url: "",
        type,
        name: file.name,
        uploading: true,
      };

      setMediaItems((prev) => [...prev, placeholder]);
      const idx = mediaItems.length + fileArray.indexOf(file);

      try {
        const result = await uploadFile(file);
        setMediaItems((prev) =>
          prev.map((m, i) =>
            m.name === file.name && m.uploading
              ? { ...m, url: result.url, type: result.type, storageId: result.storageId, uploading: false }
              : m
          )
        );
      } catch (err: any) {
        setMediaItems((prev) =>
          prev.map((m) =>
            m.name === file.name && m.uploading
              ? { ...m, uploading: false, error: err.message }
              : m
          )
        );
      }
    }
  }

  function removeMedia(index: number) {
    setMediaItems((prev) => prev.filter((_, i) => i !== index));
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    setDragging(true);
  }

  function handleDragLeave(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
  }

  async function handlePublish(mode: "draft" | "publish" = "publish") {
    const validMedia = mediaItems.filter((m) => m.url && !m.uploading && !m.error);
    if (!text.trim() && !linkUrl.trim() && validMedia.length === 0) {
      setError("Enter some text, a link URL, or add media");
      return;
    }
    if (selectedIds.length === 0) {
      setError("Select at least one account");
      return;
    }
    if (mediaItems.some((m) => m.uploading)) {
      setError("Wait for uploads to finish");
      return;
    }

    setSubmitMode(mode);
    setPublishing(true);
    setError("");

    try {
      const payload: any = { mode, account_ids: selectedIds };
      if (text.trim()) payload.text = text.trim();
      if (linkUrl.trim()) payload.link_url = linkUrl.trim();
      if (validMedia.length > 0) payload.media_storage_ids = validMedia.map((m) => m.storageId).filter(Boolean);

      // Build platform_overrides from platform-specific fields
      const overrides: Record<string, any> = {};
      if (hasYoutube) {
        const yt = platformOverrides.youtube || {};
        const ytO: any = {};
        if (yt.title?.trim()) ytO.title = yt.title.trim();
        if (yt.tags?.length) ytO.tags = yt.tags;
        if (yt.privacyStatus && yt.privacyStatus !== "public") ytO.privacyStatus = yt.privacyStatus;
        if (yt.categoryId && yt.categoryId !== "22") ytO.categoryId = yt.categoryId;
        if (Object.keys(ytO).length > 0) overrides.youtube = ytO;
      }
      if (hasReddit) {
        const rd = platformOverrides.reddit || {};
        if (rd.title?.trim()) overrides.reddit = { title: rd.title.trim() };
      }
      if (hasPinterest) {
        const pt = platformOverrides.pinterest || {};
        if (pt.title?.trim()) overrides.pinterest = { title: pt.title.trim() };
      }
      if (hasTiktok) {
        const tk = platformOverrides.tiktok || {};
        const tkO: any = {};
        if (tk.title?.trim()) tkO.title = tk.title.trim();
        if (tk.text?.trim()) tkO.text = tk.text.trim();
        if (tk.privacyLevel && tk.privacyLevel !== "PUBLIC_TO_EVERYONE") tkO.privacyLevel = tk.privacyLevel;
        if (tk.disableComment) tkO.disableComment = true;
        if (tk.autoAddMusic) tkO.autoAddMusic = true;
        if (Object.keys(tkO).length > 0) overrides.tiktok = tkO;
      }
      if (hasInstagram && platformOverrides.instagram?.post_format === "story") {
        overrides.instagram = { ...(overrides.instagram || {}), post_format: "story" };
      }
      if (hasFacebook && platformOverrides.facebook?.post_format === "story") {
        overrides.facebook = { ...(overrides.facebook || {}), post_format: "story" };
      }
      if (Object.keys(overrides).length > 0) {
        payload.platform_overrides = overrides;
      }

      if (mode === "publish" && scheduleEnabled && scheduleDate && scheduleTime) {
        const scheduledFor = new Date(`${scheduleDate}T${scheduleTime}`);
        if (scheduledFor <= new Date()) {
          setError("Scheduled time must be in the future");
          setPublishing(false);
          return;
        }
        payload.scheduled_for = scheduledFor.toISOString();
        payload.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      }

      await social.createPost(payload);
      navigate(`/posts`);
    } catch (err: any) {
      setError(err.message);
      setPublishing(false);
    }
  }

  const charCount = text.length;
  const selectedAccounts = accounts.filter((a) => selectedIds.includes(a.id));
  const hasTwitter = selectedAccounts.some((a) => a.platform === "twitter");
  const hasInstagram = selectedAccounts.some((a) => a.platform === "instagram");
  const hasTiktok = selectedAccounts.some((a) => a.platform === "tiktok");
  const hasYoutube = selectedAccounts.some((a) => a.platform === "youtube");
  const hasPinterest = selectedAccounts.some((a) => a.platform === "pinterest");
  const hasReddit = selectedAccounts.some((a) => a.platform === "reddit");
  const hasLinkedin = selectedAccounts.some((a) => a.platform === "linkedin");
  const hasFacebook = selectedAccounts.some((a) => a.platform === "facebook");
  const hasThreads = selectedAccounts.some((a) => a.platform === "threads");

  // Platform character limits (for the main post text)
  const PLATFORM_LIMITS: { platform: string; label: string; limit: number; active: boolean; icon: string }[] = [
    { platform: "twitter", label: "X", limit: 280, active: hasTwitter, icon: "𝕏" },
    { platform: "threads", label: "TH", limit: 500, active: hasThreads, icon: "@" },
    { platform: "instagram", label: "IG", limit: 2200, active: hasInstagram, icon: "📷" },
    { platform: "linkedin", label: "LI", limit: 3000, active: hasLinkedin, icon: "in" },
    { platform: "tiktok", label: "TT", limit: 2200, active: hasTiktok, icon: "♪" },
    { platform: "facebook", label: "FB", limit: 63206, active: hasFacebook, icon: "f" },
  ];
  const activeLimits = PLATFORM_LIMITS.filter(p => p.active);
  const overLimitPlatforms = activeLimits.filter(p => charCount > p.limit);

  const validMedia = mediaItems.filter((m) => m.url && !m.uploading && !m.error);
  const hasImages = validMedia.some((m) => m.type === "image");
  const hasVideo = validMedia.some((m) => m.type === "video");
  const anyUploading = mediaItems.some((m) => m.uploading);

  const platformWarnings: string[] = [];
  if (hasInstagram && !hasImages && !hasVideo) {
    platformWarnings.push("Instagram requires at least one image or video");
  }
  if (hasTiktok && !hasImages && !hasVideo) {
    platformWarnings.push("TikTok requires media (photo or video)");
  }
  if (hasYoutube && !hasVideo) {
    platformWarnings.push("YouTube requires a video");
  }
  if (hasPinterest && !hasImages) {
    platformWarnings.push("Pinterest requires an image");
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-stone-900 dark:text-white tracking-tight">Compose Post</h2>
        <p className="text-sm text-stone-500 dark:text-zinc-500 mt-1">Create and publish to multiple platforms</p>
      </div>

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400 rounded-xl mb-4 text-sm border border-red-200/60 dark:border-red-500/20">
          {error}
        </div>
      )}

      {/* Text input */}
      <div className="mb-5">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="What's on your mind?"
          rows={5}
          className="w-full px-4 py-3.5 rounded-2xl border border-stone-200 dark:border-zinc-700 bg-white dark:bg-zinc-800/50 text-stone-900 dark:text-white placeholder-stone-400 dark:placeholder-zinc-600 focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 dark:focus:border-orange-500 resize-none text-base transition-colors"
        />
        {/* Character limits per platform */}
        {charCount > 0 && activeLimits.length > 0 ? (
          <div className="mt-2 space-y-1.5">
            {activeLimits.filter(p => p.limit <= 3000 || charCount > p.limit * 0.8).map(p => {
              const pct = Math.min(charCount / p.limit, 1);
              const isOver = charCount > p.limit;
              const isWarn = pct >= 0.8 && !isOver;
              return (
                <div key={p.platform} className="flex items-center gap-2 text-xs">
                  <span className={`w-6 text-center font-semibold ${isOver ? "text-red-500" : isWarn ? "text-amber-500" : "text-stone-400 dark:text-zinc-500"}`}>{p.label}</span>
                  <div className="flex-1 h-1.5 bg-stone-100 dark:bg-zinc-800 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${isOver ? "bg-red-500" : isWarn ? "bg-amber-400" : "bg-emerald-400 dark:bg-emerald-500"}`}
                      style={{ width: `${Math.min(pct * 100, 100)}%` }}
                    />
                  </div>
                  <span className={`w-20 text-right tabular-nums ${isOver ? "text-red-500 font-semibold" : isWarn ? "text-amber-500" : "text-stone-400 dark:text-zinc-500"}`}>
                    {charCount.toLocaleString()}/{p.limit.toLocaleString()}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="mt-1.5 text-xs text-stone-400 dark:text-zinc-600">
            {charCount} characters{activeLimits.length === 0 && selectedIds.length > 0 ? "" : ""}
          </div>
        )}
      </div>

      {/* Media upload */}
      <div className="mb-5">
        <h3 className="text-xs font-semibold text-stone-500 dark:text-zinc-500 mb-2 uppercase tracking-wide">
          Media {validMedia.length > 0 && `(${validMedia.length})`}
        </h3>

        {/* Upload area */}
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all ${
            dragging
              ? "border-orange-400 bg-orange-50 dark:bg-orange-500/10"
              : "border-stone-200 dark:border-zinc-700 hover:border-stone-300 dark:hover:border-zinc-600"
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            onChange={(e) => e.target.files && handleFiles(e.target.files)}
            className="hidden"
          />
          <div className="text-stone-400 dark:text-zinc-500">
            <svg className="w-8 h-8 mx-auto mb-2 text-stone-300 dark:text-zinc-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-sm font-medium">
              {dragging ? "Drop files here" : "Click to upload or drag and drop"}
            </p>
            <p className="text-xs mt-1">Images or Videos</p>
          </div>
        </div>

        {/* Media previews */}
        {mediaItems.length > 0 && (
          <div className="grid grid-cols-3 gap-2 mt-3">
            {mediaItems.map((media, i) => (
              <div
                key={i}
                className="relative group rounded-xl overflow-hidden border border-stone-200 dark:border-zinc-700 bg-stone-100 dark:bg-zinc-800"
              >
                {media.uploading ? (
                  <div className="aspect-square flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-1"></div>
                      <p className="text-xs text-stone-500 dark:text-zinc-500 truncate px-2">{media.name}</p>
                    </div>
                  </div>
                ) : media.error ? (
                  <div className="aspect-square flex items-center justify-center p-2">
                    <div className="text-center">
                      <p className="text-xs text-red-500 font-medium">Failed</p>
                      <p className="text-xs text-stone-400 dark:text-zinc-600 truncate">{media.name}</p>
                    </div>
                  </div>
                ) : media.type === "image" ? (
                  <img src={media.url} alt={media.name} className="aspect-square object-cover w-full" />
                ) : (
                  <video src={media.url} controls className="aspect-square object-cover w-full bg-black" />
                )}

                {!media.uploading && !media.error && (
                  <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 text-[10px] font-semibold rounded-md bg-black/60 text-white uppercase">
                    {media.type}
                  </span>
                )}

                <button
                  onClick={(e) => { e.stopPropagation(); removeMedia(i); }}
                  className="absolute top-1.5 right-1.5 w-5 h-5 bg-black/60 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  x
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Link URL */}
      <div className="mb-6">
        <input
          type="url"
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
          placeholder="Link URL (optional)"
          className="w-full px-4 py-2.5 rounded-xl border border-stone-200 dark:border-zinc-700 bg-white dark:bg-zinc-800/50 text-stone-900 dark:text-white placeholder-stone-400 dark:placeholder-zinc-600 focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 dark:focus:border-orange-500 text-sm transition-colors"
        />
      </div>

      {/* Platform warnings */}
      {platformWarnings.length > 0 && (
        <div className="p-3 bg-amber-50 dark:bg-amber-500/10 border border-amber-200/60 dark:border-amber-500/20 rounded-xl mb-4">
          {platformWarnings.map((warning, i) => (
            <p key={i} className="text-xs text-amber-700 dark:text-amber-400">{warning}</p>
          ))}
        </div>
      )}

      {/* Platform-specific settings */}
      {(hasYoutube || hasReddit || hasPinterest || hasTiktok || hasInstagram || hasFacebook) && (
        <div className="mb-6 space-y-3">
          <h3 className="text-xs font-semibold text-stone-500 dark:text-zinc-500 uppercase tracking-wide">
            Platform Settings
          </h3>

          {/* Instagram settings */}
          {hasInstagram && (
            <div className="p-4 bg-white dark:bg-zinc-900 rounded-2xl border border-stone-200/60 dark:border-zinc-800/60">
              <div className="flex items-center gap-2 mb-3">
                <PlatformBadge platform="instagram" />
                <span className="text-sm font-semibold text-stone-700 dark:text-zinc-300">Instagram</span>
              </div>
              <div>
                <label className="text-xs text-stone-500 dark:text-zinc-500 mb-1.5 block">Post format</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: "feed", label: "Feed / Reel", hint: hasVideo ? "Video → Reel" : "Image → Feed" },
                    { value: "story", label: "Story", hint: "Single media · 24h" },
                  ].map((opt) => {
                    const current = platformOverrides.instagram?.post_format || "feed";
                    const selected = current === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setPlatformField("instagram", "post_format", opt.value)}
                        className={`px-3 py-2.5 rounded-xl text-sm font-medium transition-all border text-left ${
                          selected
                            ? "border-orange-300 dark:border-orange-500/40 bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-300"
                            : "border-stone-200 dark:border-zinc-700 bg-white dark:bg-zinc-800/50 text-stone-600 dark:text-zinc-400 hover:border-stone-300 dark:hover:border-zinc-600"
                        }`}
                      >
                        <div>{opt.label}</div>
                        <div className="text-xs text-stone-400 dark:text-zinc-500 mt-0.5">{opt.hint}</div>
                      </button>
                    );
                  })}
                </div>
                {platformOverrides.instagram?.post_format === "story" && (
                  <p className="text-xs text-stone-500 dark:text-zinc-500 mt-2">
                    Stories require a single image (JPEG) or video (9:16, 3–60s).
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Facebook settings */}
          {hasFacebook && (
            <div className="p-4 bg-white dark:bg-zinc-900 rounded-2xl border border-stone-200/60 dark:border-zinc-800/60">
              <div className="flex items-center gap-2 mb-3">
                <PlatformBadge platform="facebook" />
                <span className="text-sm font-semibold text-stone-700 dark:text-zinc-300">Facebook</span>
              </div>
              <div>
                <label className="text-xs text-stone-500 dark:text-zinc-500 mb-1.5 block">Post format</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: "feed", label: "Feed", hint: "Standard page post" },
                    { value: "story", label: "Story", hint: "Photo or video · 24h" },
                  ].map((opt) => {
                    const current = platformOverrides.facebook?.post_format || "feed";
                    const selected = current === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setPlatformField("facebook", "post_format", opt.value)}
                        className={`px-3 py-2.5 rounded-xl text-sm font-medium transition-all border text-left ${
                          selected
                            ? "border-orange-300 dark:border-orange-500/40 bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-300"
                            : "border-stone-200 dark:border-zinc-700 bg-white dark:bg-zinc-800/50 text-stone-600 dark:text-zinc-400 hover:border-stone-300 dark:hover:border-zinc-600"
                        }`}
                      >
                        <div>{opt.label}</div>
                        <div className="text-xs text-stone-400 dark:text-zinc-500 mt-0.5">{opt.hint}</div>
                      </button>
                    );
                  })}
                </div>
                {platformOverrides.facebook?.post_format === "story" && (
                  <p className="text-xs text-stone-500 dark:text-zinc-500 mt-2">
                    Stories require media. Videos must be 9:16, 1080×1920, 3–90s.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* YouTube settings */}
          {hasYoutube && (
            <div className="p-4 bg-white dark:bg-zinc-900 rounded-2xl border border-stone-200/60 dark:border-zinc-800/60">
              <div className="flex items-center gap-2 mb-3">
                <PlatformBadge platform="youtube" />
                <span className="text-sm font-semibold text-stone-700 dark:text-zinc-300">YouTube</span>
                {!hasVideo && <span className="text-xs text-amber-500 ml-auto">Needs video</span>}
              </div>
              <div className="space-y-3">
                <div>
                  <input
                    type="text"
                    value={platformOverrides.youtube?.title || ""}
                    onChange={(e) => setPlatformField("youtube", "title", e.target.value)}
                    placeholder="Video title"
                    maxLength={100}
                    className="w-full px-3 py-2 rounded-xl border border-stone-200 dark:border-zinc-700 bg-white dark:bg-zinc-800/50 text-stone-900 dark:text-white placeholder-stone-400 dark:placeholder-zinc-600 focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 dark:focus:border-orange-500 text-sm transition-colors"
                  />
                  <div className="flex justify-between mt-1 text-xs text-stone-400 dark:text-zinc-600">
                    <span>Falls back to post text if blank</span>
                    <span className={(platformOverrides.youtube?.title?.length || 0) > 90 ? "text-amber-500" : ""}>
                      {platformOverrides.youtube?.title?.length || 0}/100
                    </span>
                  </div>
                </div>
                <div>
                  <textarea
                    value={platformOverrides.youtube?.description || ""}
                    onChange={(e) => setPlatformField("youtube", "description", e.target.value)}
                    placeholder="Video description (falls back to post text if blank)"
                    rows={3}
                    maxLength={5000}
                    className="w-full px-3 py-2 rounded-xl border border-stone-200 dark:border-zinc-700 bg-white dark:bg-zinc-800/50 text-stone-900 dark:text-white placeholder-stone-400 dark:placeholder-zinc-600 focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 dark:focus:border-orange-500 text-sm transition-colors resize-none"
                  />
                  <div className="flex justify-between mt-1 text-xs text-stone-400 dark:text-zinc-600">
                    <span>Falls back to post text if blank</span>
                    <span className={(platformOverrides.youtube?.description?.length || 0) > 4500 ? "text-amber-500" : ""}>
                      {platformOverrides.youtube?.description?.length || 0}/5000
                    </span>
                  </div>
                </div>
                <input
                  type="text"
                  value={(platformOverrides.youtube?.tags || []).join(", ")}
                  onChange={(e) => {
                    const tags = e.target.value.split(",").map((t: string) => t.trim()).filter(Boolean);
                    setPlatformField("youtube", "tags", tags);
                  }}
                  placeholder="Tags (comma-separated)"
                  className="w-full px-3 py-2 rounded-xl border border-stone-200 dark:border-zinc-700 bg-white dark:bg-zinc-800/50 text-stone-900 dark:text-white placeholder-stone-400 dark:placeholder-zinc-600 focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 dark:focus:border-orange-500 text-sm transition-colors"
                />
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="text-xs text-stone-500 dark:text-zinc-500 mb-1 block">Privacy</label>
                    <select
                      value={platformOverrides.youtube?.privacyStatus || "public"}
                      onChange={(e) => setPlatformField("youtube", "privacyStatus", e.target.value)}
                      className="w-full px-3 py-2 rounded-xl border border-stone-200 dark:border-zinc-700 bg-white dark:bg-zinc-800/50 text-stone-900 dark:text-white text-sm focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 dark:focus:border-orange-500 transition-colors"
                    >
                      <option value="public">Public</option>
                      <option value="unlisted">Unlisted</option>
                      <option value="private">Private</option>
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="text-xs text-stone-500 dark:text-zinc-500 mb-1 block">Category</label>
                    <select
                      value={platformOverrides.youtube?.categoryId || "22"}
                      onChange={(e) => setPlatformField("youtube", "categoryId", e.target.value)}
                      className="w-full px-3 py-2 rounded-xl border border-stone-200 dark:border-zinc-700 bg-white dark:bg-zinc-800/50 text-stone-900 dark:text-white text-sm focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 dark:focus:border-orange-500 transition-colors"
                    >
                      {YOUTUBE_CATEGORIES.map((cat) => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Reddit settings */}
          {hasReddit && (
            <div className="p-4 bg-white dark:bg-zinc-900 rounded-2xl border border-stone-200/60 dark:border-zinc-800/60">
              <div className="flex items-center gap-2 mb-3">
                <PlatformBadge platform="reddit" />
                <span className="text-sm font-semibold text-stone-700 dark:text-zinc-300">Reddit</span>
              </div>
              <div>
                <input
                  type="text"
                  value={platformOverrides.reddit?.title || ""}
                  onChange={(e) => setPlatformField("reddit", "title", e.target.value)}
                  placeholder="Post title"
                  maxLength={300}
                  className="w-full px-3 py-2 rounded-xl border border-stone-200 dark:border-zinc-700 bg-white dark:bg-zinc-800/50 text-stone-900 dark:text-white placeholder-stone-400 dark:placeholder-zinc-600 focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 dark:focus:border-orange-500 text-sm transition-colors"
                />
                <div className="flex justify-between mt-1 text-xs text-stone-400 dark:text-zinc-600">
                  <span>Falls back to post text if blank</span>
                  <span className={(platformOverrides.reddit?.title?.length || 0) > 270 ? "text-amber-500" : ""}>
                    {platformOverrides.reddit?.title?.length || 0}/300
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Pinterest settings */}
          {hasPinterest && (
            <div className="p-4 bg-white dark:bg-zinc-900 rounded-2xl border border-stone-200/60 dark:border-zinc-800/60">
              <div className="flex items-center gap-2 mb-3">
                <PlatformBadge platform="pinterest" />
                <span className="text-sm font-semibold text-stone-700 dark:text-zinc-300">Pinterest</span>
              </div>
              <input
                type="text"
                value={platformOverrides.pinterest?.title || ""}
                onChange={(e) => setPlatformField("pinterest", "title", e.target.value)}
                placeholder="Pin title"
                maxLength={100}
                className="w-full px-3 py-2 rounded-xl border border-stone-200 dark:border-zinc-700 bg-white dark:bg-zinc-800/50 text-stone-900 dark:text-white placeholder-stone-400 dark:placeholder-zinc-600 focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 dark:focus:border-orange-500 text-sm transition-colors"
              />
              <p className="mt-1 text-xs text-stone-400 dark:text-zinc-600">Falls back to post text if blank</p>
            </div>
          )}

          {/* TikTok settings */}
          {hasTiktok && (
            <div className="p-4 bg-white dark:bg-zinc-900 rounded-2xl border border-stone-200/60 dark:border-zinc-800/60">
              <div className="flex items-center gap-2 mb-3">
                <PlatformBadge platform="tiktok" />
                <span className="text-sm font-semibold text-stone-700 dark:text-zinc-300">TikTok</span>
                {!hasImages && !hasVideo && <span className="text-xs text-amber-500 ml-auto">Needs media</span>}
              </div>
              <div className="space-y-3">
                {hasImages && !hasVideo && (
                  <input
                    type="text"
                    value={platformOverrides.tiktok?.title || ""}
                    onChange={(e) => setPlatformField("tiktok", "title", e.target.value)}
                    placeholder="Title (optional heading above caption)"
                    maxLength={90}
                    className="w-full px-3 py-2 rounded-xl border border-stone-200 dark:border-zinc-700 bg-white dark:bg-zinc-800/50 text-stone-900 dark:text-white placeholder-stone-400 dark:placeholder-zinc-600 focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 dark:focus:border-orange-500 text-sm transition-colors"
                  />
                )}
                <textarea
                  value={platformOverrides.tiktok?.text || ""}
                  onChange={(e) => setPlatformField("tiktok", "text", e.target.value)}
                  placeholder={hasVideo ? "Video caption (falls back to post text)" : "Photo caption (falls back to post text)"}
                  rows={2}
                  maxLength={hasVideo ? 2200 : 4000}
                  className="w-full px-3 py-2 rounded-xl border border-stone-200 dark:border-zinc-700 bg-white dark:bg-zinc-800/50 text-stone-900 dark:text-white placeholder-stone-400 dark:placeholder-zinc-600 focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 dark:focus:border-orange-500 text-sm transition-colors resize-none"
                />
                <div>
                  <label className="text-xs text-stone-500 dark:text-zinc-500 mb-1 block">Privacy</label>
                  <select
                    value={platformOverrides.tiktok?.privacyLevel || "PUBLIC_TO_EVERYONE"}
                    onChange={(e) => setPlatformField("tiktok", "privacyLevel", e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-stone-200 dark:border-zinc-700 bg-white dark:bg-zinc-800/50 text-stone-900 dark:text-white text-sm focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 dark:focus:border-orange-500 transition-colors"
                  >
                    <option value="PUBLIC_TO_EVERYONE">Public</option>
                    <option value="MUTUAL_FOLLOW_FRIENDS">Friends only</option>
                    <option value="FOLLOWER_OF_CREATOR">Followers only</option>
                    <option value="SELF_ONLY">Only me</option>
                  </select>
                </div>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={platformOverrides.tiktok?.disableComment || false}
                      onChange={(e) => setPlatformField("tiktok", "disableComment", e.target.checked)}
                      className="w-4 h-4 rounded border-stone-300 dark:border-zinc-600 text-orange-500 focus:ring-orange-500/40"
                    />
                    <span className="text-sm text-stone-700 dark:text-zinc-300">Disable comments</span>
                  </label>
                  {hasImages && !hasVideo && (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={platformOverrides.tiktok?.autoAddMusic || false}
                        onChange={(e) => setPlatformField("tiktok", "autoAddMusic", e.target.checked)}
                        className="w-4 h-4 rounded border-stone-300 dark:border-zinc-600 text-orange-500 focus:ring-orange-500/40"
                      />
                      <span className="text-sm text-stone-700 dark:text-zinc-300">Auto-add music</span>
                    </label>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Account selection */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-semibold text-stone-500 dark:text-zinc-500 uppercase tracking-wide">
            Post to ({selectedIds.length}/{accounts.length})
          </h3>
          <div className="flex gap-3 text-xs">
            <button onClick={selectAll} className="text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 font-semibold">All</button>
            <button onClick={selectNone} className="text-stone-400 dark:text-zinc-600 hover:text-stone-600 dark:hover:text-zinc-400 font-medium">None</button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-6">
            <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : accounts.length === 0 ? (
          <div className="p-6 bg-white dark:bg-zinc-900 rounded-2xl border border-stone-200/60 dark:border-zinc-800/60 text-center">
            <p className="text-stone-500 dark:text-zinc-500 text-sm mb-2">No connected accounts</p>
            <button onClick={() => navigate("/accounts")} className="text-orange-600 dark:text-orange-400 text-sm hover:text-orange-700 dark:hover:text-orange-300 font-semibold">
              Connect an account
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {accounts.map((account) => {
              const selected = selectedIds.includes(account.id);
              return (
                <button
                  key={account.id}
                  onClick={() => toggleAccount(account.id)}
                  className={`flex items-center gap-2 px-3.5 py-2 rounded-xl border text-sm transition-all ${
                    selected
                      ? "border-orange-300 dark:border-orange-500/30 bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-300"
                      : "border-stone-200 dark:border-zinc-700 bg-white dark:bg-zinc-800/50 text-stone-600 dark:text-zinc-400 hover:border-stone-300 dark:hover:border-zinc-600"
                  }`}
                >
                  <PlatformBadge platform={account.platform} />
                  <span className="font-medium truncate max-w-[120px]">
                    {account.platform_display_name || account.platform_username || account.platform}
                  </span>
                  {selected && (
                    <span className="text-orange-500 dark:text-orange-400 text-xs">&#10003;</span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Schedule section */}
      <div className="mb-6">
        <button
          onClick={() => {
            setScheduleEnabled(!scheduleEnabled);
            if (!scheduleEnabled && !scheduleDate && !scheduleTime) {
              // Default to tomorrow 9am
              const tomorrow = new Date();
              tomorrow.setDate(tomorrow.getDate() + 1);
              setScheduleDate(tomorrow.toISOString().split("T")[0]);
              setScheduleTime("09:00");
            }
          }}
          className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl border text-sm font-medium transition-all w-full ${
            scheduleEnabled
              ? "border-orange-300 dark:border-orange-500/30 bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-300"
              : "border-stone-200 dark:border-zinc-700 bg-white dark:bg-zinc-800/50 text-stone-600 dark:text-zinc-400 hover:border-stone-300 dark:hover:border-zinc-600"
          }`}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="flex-1 text-left">
            {scheduleEnabled && scheduleDate && scheduleTime
              ? (() => {
                  const d = new Date(`${scheduleDate}T${scheduleTime}`);
                  return `Scheduled: ${d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })} at ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
                })()
              : "Schedule for later"}
          </span>
          {scheduleEnabled && (
            <span className="text-xs text-orange-400">Change</span>
          )}
        </button>

        {scheduleEnabled && (
          <div className="mt-3 p-4 bg-white dark:bg-zinc-900 rounded-2xl border border-stone-200 dark:border-zinc-700">
            {/* Quick select shortcuts */}
            <div className="mb-4">
              <p className="text-xs font-semibold text-stone-500 dark:text-zinc-400 mb-2.5 uppercase tracking-wide">Quick select</p>
              <div className="grid grid-cols-3 gap-2">
                {(() => {
                  const shortcuts: { label: string; getDate: () => Date }[] = [];
                  const now = new Date();
                  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

                  const today12 = new Date(today); today12.setHours(12, 0);
                  const today17 = new Date(today); today17.setHours(17, 0);
                  const today20 = new Date(today); today20.setHours(20, 0);
                  if (today12 > now) shortcuts.push({ label: "Today 12:00", getDate: () => today12 });
                  if (today17 > now) shortcuts.push({ label: "Today 17:00", getDate: () => today17 });
                  if (today20 > now) shortcuts.push({ label: "Today 20:00", getDate: () => today20 });

                  const tmr = new Date(today); tmr.setDate(tmr.getDate() + 1);
                  const tmr9 = new Date(tmr); tmr9.setHours(9, 0);
                  const tmr12 = new Date(tmr); tmr12.setHours(12, 0);
                  const tmr18 = new Date(tmr); tmr18.setHours(18, 0);
                  shortcuts.push({ label: "Tomorrow 9:00", getDate: () => tmr9 });
                  shortcuts.push({ label: "Tomorrow 12:00", getDate: () => tmr12 });
                  shortcuts.push({ label: "Tomorrow 18:00", getDate: () => tmr18 });

                  const nextMon = new Date(today);
                  nextMon.setDate(nextMon.getDate() + ((8 - nextMon.getDay()) % 7 || 7));
                  const nextMon9 = new Date(nextMon); nextMon9.setHours(9, 0);
                  if (nextMon9 > now) shortcuts.push({ label: "Mon 9:00", getDate: () => nextMon9 });

                  const in1h = new Date(now.getTime() + 60 * 60 * 1000); in1h.setMinutes(0, 0, 0);
                  shortcuts.push({ label: "In ~1 hour", getDate: () => in1h });
                  const in3h = new Date(now.getTime() + 3 * 60 * 60 * 1000); in3h.setMinutes(0, 0, 0);
                  shortcuts.push({ label: "In ~3 hours", getDate: () => in3h });

                  return shortcuts.slice(0, 6).map((s, i) => {
                    const d = s.getDate();
                    const dStr = d.toISOString().split("T")[0];
                    const tStr = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
                    const isSelected = scheduleDate === dStr && scheduleTime === tStr;
                    return (
                      <button
                        key={i}
                        onClick={() => { setScheduleDate(dStr); setScheduleTime(tStr); }}
                        className={`px-2.5 py-2 rounded-lg text-xs font-semibold transition-all ${
                          isSelected
                            ? "bg-orange-500 text-white shadow-sm"
                            : "bg-stone-100 dark:bg-zinc-800 text-stone-600 dark:text-zinc-300 hover:bg-stone-200 dark:hover:bg-zinc-700 border border-transparent dark:border-zinc-700"
                        }`}
                      >
                        {s.label}
                      </button>
                    );
                  });
                })()}
              </div>
            </div>

            {/* Custom date/time with datetime-local for better dark mode support */}
            <div>
              <p className="text-xs font-semibold text-stone-500 dark:text-zinc-400 mb-2.5 uppercase tracking-wide">Custom date & time</p>
              <input
                type="datetime-local"
                value={scheduleDate && scheduleTime ? `${scheduleDate}T${scheduleTime}` : ""}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val) {
                    const [d, t] = val.split("T");
                    setScheduleDate(d);
                    setScheduleTime(t);
                  }
                }}
                min={new Date().toISOString().slice(0, 16)}
                style={{ colorScheme: "dark" }}
                className="w-full px-4 py-2.5 rounded-xl border border-stone-200 dark:border-zinc-700 bg-stone-50 dark:bg-zinc-800 text-stone-900 dark:text-white text-sm focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 dark:focus:border-orange-500 transition-colors [color-scheme:light] dark:[color-scheme:dark]"
              />
              <p className="text-xs text-stone-400 dark:text-zinc-500 mt-2">
                Timezone: {Intl.DateTimeFormat().resolvedOptions().timeZone}
              </p>
            </div>

            {/* Clear schedule */}
            <button
              onClick={() => { setScheduleEnabled(false); setScheduleDate(""); setScheduleTime(""); }}
              className="mt-3 text-xs text-stone-400 dark:text-zinc-500 hover:text-red-500 dark:hover:text-red-400 font-medium transition-colors"
            >
              Remove schedule
            </button>
          </div>
        )}
      </div>

      {/* Draft / Publish actions */}
      <div className="grid grid-cols-[auto_1fr] gap-3">
        <button
          onClick={() => handlePublish("draft")}
          disabled={publishing || anyUploading || selectedIds.length === 0 || (!text.trim() && !linkUrl.trim() && validMedia.length === 0)}
          className="px-5 py-3.5 rounded-xl border border-stone-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-stone-700 dark:text-zinc-300 font-semibold hover:border-stone-300 dark:hover:border-zinc-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {publishing && submitMode === "draft" ? "Saving…" : "Save draft"}
        </button>
        <button
          onClick={() => handlePublish("publish")}
          disabled={publishing || anyUploading || selectedIds.length === 0 || (!text.trim() && !linkUrl.trim() && validMedia.length === 0) || (scheduleEnabled && (!scheduleDate || !scheduleTime))}
          className={`py-3.5 text-white rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all ${
            scheduleEnabled
              ? "bg-indigo-500 hover:bg-indigo-600"
              : "bg-orange-500 hover:bg-orange-600"
          }`}
        >
          {publishing && submitMode === "publish"
            ? scheduleEnabled ? "Scheduling..." : "Publishing..."
            : anyUploading
              ? "Uploading media..."
              : scheduleEnabled
                ? `Schedule for ${selectedIds.length} account${selectedIds.length !== 1 ? "s" : ""}`
                : `Publish to ${selectedIds.length} account${selectedIds.length !== 1 ? "s" : ""}`}
        </button>
      </div>
    </div>
  );
}
